/**
 * GoogleSignInButton — Vague 107 (basic-scope SSO only)
 *
 * Why no Gmail/Calendar scopes here? Those are "sensitive" and Google requires
 * either (a) the user to be in the Test Users whitelist, or (b) the app to be
 * verified. Asking for them at login means EVERY new team member must be added
 * to Google Cloud Console manually — bad UX.
 *
 * Solution: this button asks for `openid email profile` ONLY (non-sensitive),
 * which works for any Google user as long as the OAuth Consent Screen is in
 * "In production" status. Gmail/Calendar sync is a separate opt-in OAuth
 * triggered from Profile → Connexion Google → "Activer Gmail/Agenda".
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../api/client';
import { oauthRedirect } from '../utils/oauthRedirect';

const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const FALLBACK_SSO_SCOPES = ['openid', 'email', 'profile'];

function genNonce() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function GoogleSignInButton({ onError }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Wave 1u3 — retry up to 3 times with exponential back-off so the button
    // appears reliably even on cold-start when the backend hasn't fully
    // booted yet (user reported "il faut rafraîchir plusieurs fois" on prod).
    let cancelled = false;
    let attempt = 0;
    const tryFetch = () => {
      api
        .get('/integrations/google/config')
        .then(({ data }) => { if (!cancelled) setConfig(data); })
        .catch(() => {
          attempt += 1;
          if (cancelled) return;
          if (attempt < 3) {
            setTimeout(tryFetch, 800 * attempt);  // 800ms, 1.6s
          } else {
            setConfig({ configured: false });
          }
        });
    };
    tryFetch();
    return () => { cancelled = true; };
  }, []);

  const handleClick = () => {
    if (!config?.configured || !config?.client_id) {
      onError?.('Google OAuth non configuré côté serveur.');
      return;
    }
    setLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    const redirectUri = `${window.location.origin}/auth/google`;
    const state = genNonce();
    sessionStorage.setItem('google_oauth_state', state);

    // Vague 107 — Login uses BASIC scopes only (no Gmail/Calendar). Lets any
    // admin-created user log in via Google without being whitelisted on
    // Google Cloud Console > Test Users.
    const scopes = (config.scopes_sso && config.scopes_sso.length)
      ? config.scopes_sso
      : FALLBACK_SSO_SCOPES;

    const params = new URLSearchParams({
      client_id: config.client_id,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: scopes.join(' '),
      // No `access_type=offline` for the SSO flow — we don't need a long-lived
      // refresh_token here. Gmail/Calendar opt-in flow asks for offline.
      prompt: 'select_account',
      state,
    });
    const url = `${GOOGLE_AUTH_URL}?${params.toString()}`;
    // Wave 1u3 — uses shared helper to handle the iframed-preview edge-case
    // (Google blocks iframe embedding via X-Frame-Options).
    oauthRedirect(url, { onAfterPopup: () => setLoading(false) });
  };

  if (config === null) {
    return (
      <div className="w-full h-11 rounded-xl bg-[#F6F7FB] flex items-center justify-center" data-testid="google-signin-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#8E8FA5]" />
      </div>
    );
  }
  if (!config?.configured) return null;

  return (
    <button
      type="button"
      data-testid="google-signin-btn"
      onClick={handleClick}
      disabled={loading}
      className="w-full h-11 rounded-xl bg-white border border-[#E5E7EB] text-[#1A1B2E] font-semibold text-[14px] flex items-center justify-center gap-3 hover:bg-[#FAFAFE] transition-colors disabled:opacity-60"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin text-[var(--ofm-primary)]" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.7 6 29 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-3.9z" />
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.7 6 29 4 24 4 16.3 4 9.7 8.4 6.3 14.7z" />
          <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5.1l-6-4.9c-2 1.4-4.4 2.2-6.9 2.2-5.3 0-9.8-3.4-11.3-8.1l-6.5 5C9.5 39.5 16.2 44 24 44z" />
          <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.1 4-3.9 5.3l6 4.9c4.2-3.9 6.6-9.6 6.6-16.3 0-1.3-.1-2.6-.4-3.8z" />
        </svg>
      )}
      Continuer avec Google
    </button>
  );
}
